import { getAutogeneratedBanner } from '../banners';
import {
  BLOB_ENCRYPTED_MSG,
  BLOB_MISSING_MSG,
  ENCRYPTED_BLOB_PREFIX,
  getFieldDocLines,
  getSensitiveKeys,
  partitionRepresentableKeys,
  skippedKeysComment,
  type CoercedType,
  type ResolvedFieldType,
} from '../shared';

// a lowercase key equal to a Python keyword can't be a class-syntax TypedDict field (`class: str` is
// a SyntaxError); such keys are skipped from the type (still returned by load_env() at runtime)
const PYTHON_KEYWORDS = new Set([
  'False',
  'None',
  'True',
  'and',
  'as',
  'assert',
  'async',
  'await',
  'break',
  'class',
  'continue',
  'def',
  'del',
  'elif',
  'else',
  'except',
  'finally',
  'for',
  'from',
  'global',
  'if',
  'import',
  'in',
  'is',
  'lambda',
  'nonlocal',
  'not',
  'or',
  'pass',
  'raise',
  'return',
  'try',
  'while',
  'with',
  'yield',
]);

function getPythonCoercedTypeString(coerced: CoercedType): string {
  if (coerced === 'int') return 'int';
  if (coerced === 'number') return 'float';
  if (coerced === 'boolean') return 'bool';
  if (coerced === 'object') return 'dict[str, Any]';
  if (typeof coerced === 'object' && 'enum' in coerced) {
    if (!coerced.enum.length) return 'str';
    // PEP 586 forbids float literals in `Literal[...]`, so an enum with any float member can't be
    // a Literal — fall back to the union of the member kinds present (just `float` when uniform,
    // e.g. `str | float` when mixed — the runtime value can still be a string member)
    if (coerced.enum.some((option) => typeof option === 'number' && !Number.isInteger(option))) {
      const memberKinds = new Set(coerced.enum.map((option) => {
        if (typeof option === 'string') return 'str';
        if (typeof option === 'boolean') return 'bool';
        return Number.isInteger(option) ? 'int' : 'float';
      }));
      return (['str', 'int', 'float', 'bool'] as const).filter((k) => memberKinds.has(k)).join(' | ');
    }
    const options = coerced.enum.map((option) => {
      if (typeof option === 'string') return JSON.stringify(option);
      if (typeof option === 'boolean') return option ? 'True' : 'False';
      return String(option);
    });
    return `Literal[${options.join(', ')}]`;
  }
  // the blob value is a real JSON array/object, so list/dict annotations match json.loads output
  if (typeof coerced === 'object' && 'arrayOf' in coerced) {
    return `list[${getPythonCoercedTypeString(coerced.arrayOf)}]`;
  }
  if (typeof coerced === 'object' && 'recordOf' in coerced) {
    return `dict[str, ${coerced.recordOf.values ? getPythonCoercedTypeString(coerced.recordOf.values) : 'Any'}]`;
  }
  return 'str';
}

function formatPythonField(field: ResolvedFieldType, typeString: string): Array<string> {
  const docLines = getFieldDocLines(field);
  // single line of docs → inline comment; multiple → comment lines above the field; none → no comment
  if (docLines.length === 1) {
    return [`    ${field.key}: ${typeString}  # ${docLines[0]}`];
  }
  return [
    ...docLines.map((line) => `    # ${line}`),
    `    ${field.key}: ${typeString}`,
  ];
}

function formatPythonTypedDict(
  className: string,
  classDoc: string,
  fields: Array<ResolvedFieldType>,
  getType: (field: ResolvedFieldType) => string,
): Array<string> {
  const lines = [
    '',
    '',
    `class ${className}(TypedDict):`,
    `    """${classDoc}"""`,
  ];

  if (!fields.length) {
    lines.push('    pass');
    return lines;
  }

  for (const field of fields) {
    const baseType = getType(field);
    const typeString = field.isRequired ? baseType : `NotRequired[${baseType}]`;
    lines.push(...formatPythonField(field, typeString));
  }

  return lines;
}

export function generatePythonEnvSrc(fields: Array<ResolvedFieldType>): string {
  // typed structure covers only representable keys; skipped keys stay in load_env()'s dict at runtime
  const { safe, skipped } = partitionRepresentableKeys(fields, (k) => PYTHON_KEYWORDS.has(k));
  // SENSITIVE_KEYS covers every sensitive key (incl. skipped ones) — redaction shouldn't miss them
  const sensitiveKeys = getSensitiveKeys(fields);
  const sensitiveSet = sensitiveKeys.length
    ? `frozenset({${sensitiveKeys.map((k) => JSON.stringify(k)).join(', ')}})`
    : 'frozenset()';

  // `from __future__ import annotations` stringizes every annotation, so symbols like NotRequired /
  // Literal / Any (and subscripts like dict[str, Any]) are never evaluated at runtime — the module
  // imports on any Python 3.7+, while type checkers still read the real types from the source. The
  // type-only symbols therefore live under `TYPE_CHECKING` (imported only when actually used).
  const typeStrings = safe.map((f) => getPythonCoercedTypeString(f.coerced));
  const typeOnlyImports: Array<string> = [];
  if (typeStrings.some((t) => t.includes('Any'))) typeOnlyImports.push('Any');
  // includes (not startsWith): Literal can be nested, e.g. list[Literal["a", "b"]]
  if (typeStrings.some((t) => t.includes('Literal['))) typeOnlyImports.push('Literal');
  if (safe.some((f) => !f.isRequired)) typeOnlyImports.push('NotRequired');
  typeOnlyImports.sort();

  // `cast` is called at runtime (identity fn) so it's a real import, not type-only
  const runtimeTypingImports = ['TypedDict', 'cast'];
  if (typeOnlyImports.length) runtimeTypingImports.push('TYPE_CHECKING');
  runtimeTypingImports.sort();

  const src = [
    getAutogeneratedBanner('#'),
    'from __future__ import annotations',
    '',
    'import json',
    'import os',
    `from typing import ${runtimeTypingImports.join(', ')}`,
    ...(typeOnlyImports.length ? [
      '',
      'if TYPE_CHECKING:',
      `    from typing import ${typeOnlyImports.join(', ')}`,
    ] : []),
    ...(skipped.length ? ['', ...skippedKeysComment(skipped, '#')] : []),
    ...formatPythonTypedDict(
      'Env',
      'Coerced env schema (matches varlock coercion) — values are typed, not raw strings.',
      safe,
      (field) => getPythonCoercedTypeString(field.coerced),
    ),
    '',
    '',
    '# Keys whose values are sensitive — use this to build your own redaction / leak-scanning.',
    `SENSITIVE_KEYS: frozenset[str] = ${sensitiveSet}`,
    '',
    '',
    'def load_env() -> Env:',
    '    """Parse the `__VARLOCK_ENV` blob (injected by `varlock run`) into typed, coerced values."""',
    '    blob = os.environ.get("__VARLOCK_ENV")',
    '    if not blob:',
    `        raise RuntimeError(${JSON.stringify(BLOB_MISSING_MSG)})`,
    `    if blob.startswith(${JSON.stringify(ENCRYPTED_BLOB_PREFIX)}):`,
    `        raise RuntimeError(${JSON.stringify(BLOB_ENCRYPTED_MSG)})`,
    '    graph = json.loads(blob)',
    '    # unset optional keys are omitted from the blob (no "value"), so skip them here —',
    '    # matches the TypedDict `NotRequired` contract (key absent, not present-as-None)',
    '    return cast(',
    '        Env,',
    '        {key: entry["value"] for key, entry in graph["config"].items() if "value" in entry},',
    '    )',
    '',
  ];

  return src.join('\n');
}
